import { Component, LOCALE_ID, computed, inject, input, output } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { AppDropdown } from '../../../shared/components/dropdown/dropdown';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChartModule } from 'primeng/chart';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { appointmentStatusKey } from '../../../core/utils/appointment-status.utils';
import { OwnerDashboard, OwnerFinanceDashboard, StatusBreakdown, StatusPeriod } from '../../../core/models/clinic-dashboard.model';
import { formatNumber } from '../../../core/utils/number.utils';
import { DashStatCard } from './stat-card';

@Component({
  selector: 'app-dash-owner-view',
  standalone: true,
  imports: [CommonModule, AppDropdown, TranslateModule, ChartModule, DashStatCard],
  template: `
    <div class="space-y-6">
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <app-dash-stat-card [label]="'dash_today_appointments' | translate" icon="pi-calendar" [value]="data().today_total" />
        <app-dash-stat-card [label]="'dash_waiting' | translate" icon="pi-clock" [value]="data().flow.waiting" valueClass="text-amber-600" />
        <app-dash-stat-card [label]="'dash_in_service' | translate" icon="pi-heart" [value]="data().flow.in_service" valueClass="text-primary" />
        <app-dash-stat-card [label]="'dash_done' | translate" icon="pi-check-circle" [value]="data().flow.done" valueClass="text-emerald-600" />
      </div>

      <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <app-dash-stat-card [label]="'dash_no_show_rate_7d' | translate" icon="pi-times-circle" [value]="rate(data().attendance.last_7d.no_show, data().attendance.last_7d.total)"
          [hint]="data().attendance.last_7d.no_show + '/' + data().attendance.last_7d.total" />
        <app-dash-stat-card [label]="'dash_no_show_rate_30d' | translate" icon="pi-chart-line" [value]="rate(data().attendance.last_30d.no_show, data().attendance.last_30d.total)"
          [hint]="data().attendance.last_30d.no_show + '/' + data().attendance.last_30d.total" />
        <app-dash-stat-card [label]="'dash_new_patients_week' | translate" icon="pi-user-plus" [value]="data().new_patients.week"
          [hint]="('dash_month' | translate) + ' ' + data().new_patients.month" />
        <app-dash-stat-card [label]="'dash_waitlist' | translate" icon="pi-list" [value]="data().waitlist_count" />
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <!-- Today by status -->
        <div class="lg:col-span-2 bg-white rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100 flex flex-col">
          <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
            <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest">{{ 'dashboard_appointment_status' | translate }}</p>
            <app-dropdown>
              <ng-template #trigger>
                <button type="button"
                  class="flex items-center gap-3 px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-100 transition-all shadow-sm">
                  {{ periodLabel() }}
                  <i class="pi pi-chevron-down text-[9px] opacity-40"></i>
                </button>
              </ng-template>
              <ng-template #content>
                <div class="flex flex-col py-1 min-w-[160px]">
                  <button type="button" *ngFor="let o of periodOptions()" (click)="statusPeriodChange.emit(o.value)"
                    class="px-5 py-3 text-left text-[10px] font-black uppercase tracking-widest transition-colors flex items-center justify-between gap-4 hover:bg-slate-50"
                    [ngClass]="statusPeriod() === o.value ? 'text-primary bg-primary/5' : 'text-slate-600'">
                    {{ o.label }}
                    <i *ngIf="statusPeriod() === o.value" class="pi pi-check text-[10px]"></i>
                  </button>
                </div>
              </ng-template>
            </app-dropdown>
          </div>
          <ng-container *ngIf="chart() as c; else emptyStatus">
            <div class="flex flex-col sm:flex-row lg:flex-col xl:flex-row items-center gap-6 flex-1 transition-opacity" [class.opacity-50]="statusLoading()">
              <div class="relative w-48 h-48 shrink-0">
                <p-chart type="doughnut" [data]="c" [options]="doughnutOptions" width="100%" height="100%" />
                <div class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span class="text-3xl font-black text-slate-900">{{ total() }}</span>
                  <span class="mt-0.5 max-w-[84px] text-center text-[11px] font-semibold leading-tight text-slate-400">{{ 'dash_appointments' | translate }}</span>
                </div>
              </div>
              <ul class="w-full space-y-2.5">
                <li *ngFor="let s of statuses()" class="flex items-center justify-between text-sm">
                  <span class="flex items-center gap-2 text-slate-600">
                    <span class="w-2.5 h-2.5 rounded-full" [style.background]="s.color_bg || '#94a3b8'"></span>
                    {{ statusKey(s.status_name) | translate }}
                  </span>
                  <span class="font-black text-slate-900">{{ s.count }}</span>
                </li>
              </ul>
            </div>
          </ng-container>
          <ng-template #emptyStatus>
            <p class="text-sm text-slate-400 py-10 text-center">{{ 'dashboard_no_appointments' | translate }}</p>
          </ng-template>
        </div>

        <!-- Next 7 days -->
        <div class="lg:col-span-3 bg-white rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100 flex flex-col">
          <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">{{ 'dash_next_7_days' | translate }}</p>
          <div class="relative flex-1 min-h-[240px] sm:min-h-[260px]">
            <p-chart type="bar" [data]="weekChart()" [options]="barOptions" [plugins]="plugins" width="100%" height="100%" />
          </div>
        </div>
      </div>

      <!-- Finance: owner-only, hidden when the finance RPC is unavailable -->
      <div *ngIf="finance() as f" class="bg-white rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100">
        <div class="flex flex-wrap items-start justify-between gap-4 mb-4">
          <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest">{{ 'dash_received_30d' | translate }}</p>
          <div class="flex flex-wrap gap-x-8 gap-y-2">
            <div>
              <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest">{{ 'dash_received_today' | translate }}</p>
              <p class="text-lg font-black text-emerald-600">{{ money(f.received_today) }}</p>
            </div>
            <div>
              <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest">{{ 'dash_received_month' | translate }}</p>
              <p class="text-lg font-black text-emerald-600">{{ money(f.received_month) }}</p>
            </div>
            <div>
              <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest">{{ 'dash_outstanding' | translate }}</p>
              <p class="text-lg font-black text-amber-600">{{ money(f.outstanding.total) }}
                <span class="text-xs font-semibold text-slate-400">· {{ f.outstanding.debtors }} {{ 'dash_debtors' | translate }}</span></p>
            </div>
          </div>
        </div>
        <div class="relative h-64">
          <p-chart type="line" [data]="financeChart()!" [options]="lineOptions" [plugins]="[]" width="100%" height="100%" />
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
        <!-- Doctor load -->
        <div class="lg:col-span-2 bg-white rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100">
          <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">{{ 'dash_doctor_load' | translate }}</p>
          <p *ngIf="!data().doctor_load.length" class="text-sm text-slate-400 py-6 text-center">{{ 'dash_nothing_today' | translate }}</p>
          <ul class="space-y-3 max-h-80 overflow-y-auto pr-2 -mr-2">
            <li *ngFor="let d of data().doctor_load" class="flex items-center gap-3">
              <span class="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-black">{{ d.name.charAt(0) }}</span>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-bold text-slate-900 truncate">{{ d.name }}</p>
                <div class="h-1.5 rounded-full bg-slate-100 mt-1.5 overflow-hidden">
                  <div class="h-full bg-emerald-500" [style.width.%]="d.total ? (d.done / d.total) * 100 : 0"></div>
                </div>
              </div>
              <span class="text-sm font-black text-slate-700">{{ d.done }}/{{ d.total }}</span>
            </li>
          </ul>
        </div>

        <!-- Extras -->
        <div class="flex flex-col gap-4">
          <app-dash-stat-card class="flex-1" [centered]="true" [label]="'dash_doctors_today' | translate" icon="pi-briefcase"
            [value]="data().doctor_load.length" />
          <app-dash-stat-card class="flex-1" [centered]="true" [label]="'dash_cancelled_7d' | translate" icon="pi-ban" valueClass="text-red-600"
            [value]="data().attendance.last_7d.cancelled" />
          <app-dash-stat-card class="flex-1" [centered]="true" *ngIf="onlineUsers() !== null" [label]="'dashboard_online_users' | translate" icon="pi-users" [value]="onlineUsers()" />
        </div>
      </div>
    </div>
  `,
})
export class DashOwnerView {
  data = input.required<OwnerDashboard>();
  finance = input<OwnerFinanceDashboard | null>(null);
  statusPeriod = input<StatusPeriod>('today');
  /** Breakdown for a non-today period; null means use today's data from `data`. */
  statusBreakdown = input<StatusBreakdown | null>(null);
  statusLoading = input(false);
  statusPeriodChange = output<StatusPeriod>();
  onlineUsers = input<number | null>(null);

  private translate = inject(TranslateService);

  readonly statusKey = appointmentStatusKey;

  readonly periodOptions = computed(() => {
    const t = (k: string) => this.translate.instant(k);
    return [
      { label: t('period_today'), value: 'today' as StatusPeriod },
      { label: t('dash_period_week'), value: 'week' as StatusPeriod },
      { label: t('dash_period_month'), value: 'month' as StatusPeriod },
      { label: t('dash_period_quarter'), value: 'quarter' as StatusPeriod },
    ];
  });

  readonly periodLabel = computed(() => this.periodOptions().find(o => o.value === this.statusPeriod())?.label ?? '');

  readonly statuses = computed(() =>
    ((this.statusBreakdown()?.by_status ?? this.data().by_status) || []).filter(s => s.count > 0));

  readonly total = computed(() => this.statuses().reduce((sum, s) => sum + s.count, 0));

  readonly chart = computed(() => {
    const s = this.statuses();
    if (!s.length) return null;
    return {
      labels: s.map(x => this.translate.instant(appointmentStatusKey(x.status_name))),
      datasets: [{
        data: s.map(x => x.count),
        backgroundColor: s.map(x => x.color_bg || '#94a3b8'),
        borderWidth: 0,
      }],
    };
  });

  private locale = inject(LOCALE_ID);
  readonly plugins = [ChartDataLabels];

  private readonly anim = { duration: 900, easing: 'easeOutQuart' as const };

  readonly doughnutOptions = {
    maintainAspectRatio: false,
    cutout: '68%',
    animation: { ...this.anim, animateRotate: true, animateScale: true },
    plugins: { legend: { display: false }, datalabels: { display: false } },
  };

  readonly weekChart = computed(() => {
    const days = this.data().next_7_days;
    return {
      labels: days.map(d => [formatDate(d.day, 'EEE', this.locale), formatDate(d.day, 'd', this.locale)]),
      datasets: [{
        data: days.map(d => d.count),
        backgroundColor: days.map((_, i) => (i === 0 ? '#3a66af' : '#3a66af66')),
        hoverBackgroundColor: '#3a66af',
        borderRadius: 10,
        borderSkipped: false,
        maxBarThickness: 56,
      }],
    };
  });

  readonly financeChart = computed(() => {
    const f = this.finance();
    if (!f) return null;
    return {
      labels: f.daily.map(d => formatDate(d.day, 'd MMM', this.locale)),
      datasets: [{
        data: f.daily.map(d => d.total),
        borderColor: '#10b981',
        borderWidth: 2.5,
        tension: 0.4,
        fill: true,
        backgroundColor: (ctx: any) => {
          const { chart } = ctx;
          const area = chart.chartArea;
          if (!area) return '#10b98122';
          const g = chart.ctx.createLinearGradient(0, area.top, 0, area.bottom);
          g.addColorStop(0, '#10b98155');
          g.addColorStop(1, '#10b98100');
          return g;
        },
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: '#10b981',
      }],
    };
  });

  readonly lineOptions = {
    maintainAspectRatio: false,
    animation: this.anim,
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
      tooltip: {
        displayColors: false,
        callbacks: { label: (ctx: any) => this.money(ctx.parsed.y) },
      },
    },
    scales: {
      x: { grid: { display: false }, border: { display: false }, ticks: { color: '#94a3b8', maxTicksLimit: 8, maxRotation: 0 } },
      y: { beginAtZero: true, grace: '10%', border: { display: false }, grid: { color: '#f1f5f9' }, ticks: { color: '#94a3b8', precision: 0 } },
    },
  };

  readonly barOptions = {
    maintainAspectRatio: false,
    animation: this.anim,
    layout: { padding: { top: 24 } },
    plugins: {
      legend: { display: false },
      tooltip: { displayColors: false },
      datalabels: {
        anchor: 'end' as const,
        align: 'end' as const,
        color: '#334155',
        font: { weight: 'bold' as const, size: 12 },
      },
    },
    scales: {
      x: { grid: { display: false }, border: { display: false }, ticks: { color: '#94a3b8', maxRotation: 0, font: { size: 11, weight: 'bold' as const } } },
      y: { beginAtZero: true, grace: '10%', border: { display: false }, grid: { color: '#f1f5f9' }, ticks: { color: '#94a3b8', precision: 0 } },
    },
  };

  money(v: number): string {
    return '€ ' + formatNumber(Number(v) || 0);
  }

  rate(part: number, total: number): string {
    return total ? `${Math.round((part / total) * 100)}%` : '0%';
  }
}
